import {Component, computed, DestroyRef, inject, input, model, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_MATERIALS} from "@common/services/logging"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatChipsModule} from "@angular/material/chips"
import {MatDialogModule, MatDialogRef} from "@angular/material/dialog"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatIconModule} from "@angular/material/icon"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatTooltipModule} from "@angular/material/tooltip"
import {AddItemDialogComponent} from "@app/common/components/dialogs/add-item-dialog/add-item-dialog.component"
import {CheckboxComponent} from "@app/common/components/inputs/checkbox/checkbox.component"
import {PlaceholderComponent} from "@app/common/components/placeholders/placeholder/placeholder.component"
import {Accept} from "@app/common/helpers/utils/files"
import {ThumbnailLayout} from "@app/common/models/enums/thumbnail-layout"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {ImageColorSpaceLabels} from "@app/platform/models/state-labels/image-color-space-labels"
import {MaterialAiTagLabels} from "@app/platform/models/state-labels/material-ai-tag-labels"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {imageDataTypeFromTextureType} from "@app/textures/utils/color-space"
import {MaterialAiTag} from "@cm/job-nodes/material-ai"
import {ImageColorSpace} from "@cm/template-nodes"
import {IsDefined} from "@cm/utils/filter"
import {DropFilesComponent} from "@common/components/files"
import {HintComponent} from "@common/components/hint/hint.component"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {StringInputComponent} from "@common/components/inputs/string-input/string-input.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {DataObjectType, MaterialState, NextActor, PaymentState, TextureType} from "@generated"
import {
    AddMaterialDialogMaterialRangeTagsGQL,
    AddMaterialDialogTagFragment,
    AddMaterialGQL,
    AddMaterialIncludeTexturesGQL,
    AddMaterialItemWithTextureSetRevisionsFragment,
} from "@platform/components/materials/add-material-dialog/add-material-dialog.generated"
import {distinctUntilChanged, from, map, switchMap} from "rxjs"
import {z} from "zod"

export type MaterialCreateMethod = "BATCH" | "BLANK" | "FROM_IMAGE" | "FROM_IMAGE_AI" | "FROM_PBR_FILES"

export type CreateMaterialFormData = {
    name: string
    organizationId?: string
    materialRangeTagId?: string
    createMethod: MaterialCreateMethod
    width?: number
    height?: number
    pbrFiles?: Map<TextureType, {file?: File; imageUrl?: string; colorSpace: ImageColorSpace}>
    materialAiIgnoreDpi: boolean
    materialAiTags?: MaterialAiTag[]
}

const AddMaterialSchema = z.discriminatedUnion("createMethod", [
    z.object({
        createMethod: z.literal("BATCH"),
        organizationId: z.string().min(1, "Organization ID is required"),
        materialRangeTagId: z.string().optional(),
    }),
    z.object({
        createMethod: z.literal("BLANK"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        materialRangeTagId: z.string().optional(),
    }),
    z.object({
        createMethod: z.literal("FROM_PBR_FILES"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        materialRangeTagId: z.string().optional(),
    }),
    z.object({
        createMethod: z.literal("FROM_IMAGE"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        materialRangeTagId: z.string().optional(),
        width: z.number().positive("Width must be a positive number"),
        height: z.number().positive("Height must be a positive number"),
        pbrFiles: z.instanceof(Map<TextureType, {file: File; colorSpace: ImageColorSpace}>).refine((map) => map.size > 0, "PBR files are required"),
    }),
    z.object({
        createMethod: z.literal("FROM_IMAGE_AI"),
        name: z.string().min(1, "Name is required"),
        organizationId: z.string().min(1, "Organization ID is required"),
        materialRangeTagId: z.string().optional(),
        width: z.number().positive("Width must be a positive number"),
        height: z.number().positive("Height must be a positive number"),
        pbrFiles: z.instanceof(Map<TextureType, {file: File; colorSpace: ImageColorSpace}>).refine((map) => map.size > 0, "PBR files are required"),
        materialAiIgnoreDpi: z.boolean().default(false),
        materialAiTags: z.array(z.nativeEnum(MaterialAiTag)).default([]),
    }),
])

export type AddMaterialData = z.infer<typeof AddMaterialSchema>

export type CreatedMaterialType =
    | {
          createMethod: "BATCH"
          item: {organizationId: string; materialRangeTagId?: string}
      }
    | {
          createMethod: "SINGLE"
          item: AddMaterialItemWithTextureSetRevisionsFragment
      }

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-add-material-dialog",
    imports: [
        FormsModule,
        MatIconModule,
        MatChipsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatTooltipModule,
        AddItemDialogComponent,
        CheckboxComponent,
        DropFilesComponent,
        InputContainerComponent,
        NumericInputComponent,
        OrganizationSelectComponent,
        PlaceholderComponent,
        StringInputComponent,
        HintComponent,
    ],
    templateUrl: "./add-material-dialog.component.html",
    styleUrl: "./add-material-dialog.component.scss",
})
export class AddMaterialDialogComponent implements OnInit {
    readonly $isBatchMode = input(false, {alias: "isBatchMode"})

    protected readonly permission = inject(PermissionsService)
    readonly organizations = inject(OrganizationsService)
    readonly materialRangeTags = inject(AddMaterialDialogMaterialRangeTagsGQL)
    private readonly destroyRef = inject(DestroyRef)
    private readonly dialogRef = inject<MatDialogRef<AddMaterialDialogComponent, unknown>>(MatDialogRef)

    private readonly createMaterialGql = inject(AddMaterialGQL)
    private readonly createMaterialIncludeTexturesGql = inject(AddMaterialIncludeTexturesGQL)
    private readonly uploadService = inject(UploadGqlService)
    private readonly notifications = inject(NotificationsService)
    private readonly texturesApiService = inject(TexturesApiService)
    private readonly logger: CmLogger = inject(CM_LOGGER_MATERIALS)

    $can = this.permission.$to

    readonly $materialRangeTags = signal<AddMaterialDialogTagFragment[] | undefined>(undefined)

    readonly $data = model<CreateMaterialFormData>(
        {name: "", organizationId: this.organizations.$current()?.id, createMethod: "BLANK", materialAiIgnoreDpi: true},
        {alias: "data"},
    )
    data$ = toObservable(this.$data)

    readonly $isLoading = signal<boolean>(false)

    protected readonly Accept = Accept
    protected readonly TextureType = TextureType
    protected readonly ThumbnailLayout = ThumbnailLayout
    protected readonly ImageColorSpace = ImageColorSpace
    protected readonly ImageColorSpaceLabels = ImageColorSpaceLabels
    protected readonly MaterialAiTagLabels = MaterialAiTagLabels

    readonly $validatedInputData = computed(() => {
        const data = this.$data()
        if (this.$isBatchMode()) {
            data.createMethod = "BATCH"
        }
        const validatedData = AddMaterialSchema.safeParse(data)
        if (!validatedData.success) {
            return undefined
        }
        return validatedData.data
    })

    readonly $createMethods = signal<{id: MaterialCreateMethod; label: string; icon: string}[]>([
        {
            id: "BLANK",
            label: "Blank",
            icon: "fa-file",
        },
        {
            id: "FROM_IMAGE",
            label: "From Image",
            icon: "fa-image",
        },
        {
            id: "FROM_IMAGE_AI",
            label: "From Image (AI)",
            icon: "fa-hexagon-nodes",
        },
        {
            id: "FROM_PBR_FILES",
            label: "From PBR Files",
            icon: "fa-images",
        },
    ])

    ngOnInit() {
        // Fetch material range tags for the selected organization
        this.data$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                map((data) => data.organizationId),
                distinctUntilChanged(),
                switchMap((organizationId) => {
                    if (organizationId) {
                        return from(fetchThrowingErrors(this.materialRangeTags)({organizationId}).then(({tags}) => tags.filter(IsDefined)))
                    }
                    return from([])
                }),
            )
            .subscribe((rangeTags) => {
                this.$materialRangeTags.set(rangeTags)
            })
    }

    updateData = (updateData: Partial<CreateMaterialFormData>) => {
        this.$data.update((prev) => ({
            ...prev,
            ...updateData,
        }))
    }

    updateOrganization = async (data: {organizationId: string}) => {
        this.updateData({organizationId: data.organizationId, materialRangeTagId: undefined})
    }

    updatePbrImage = ({textureType, file, colorSpace}: {textureType: TextureType; file?: File; colorSpace?: ImageColorSpace}) => {
        this.$data.update((prev) => {
            const previousFile = prev.pbrFiles?.get(textureType)

            const newPbrFiles = new Map(prev.pbrFiles || [])
            newPbrFiles.set(textureType, {
                colorSpace: colorSpace || previousFile?.colorSpace || ImageColorSpace.Srgb,
                file: file || previousFile?.file || undefined,
                imageUrl: file ? URL.createObjectURL(file) : previousFile?.imageUrl,
            })

            if (previousFile?.imageUrl && file) {
                // we updated the image, clean up the object URL
                URL.revokeObjectURL(previousFile?.imageUrl)
            }

            return {
                ...prev,
                pbrFiles: newPbrFiles,
            }
        })
    }

    setSelectedMaterialAiTags = (tags: MaterialAiTag[]) => {
        this.$data.update((prev) => {
            return {...prev, materialAiTags: tags}
        })
    }

    removeMaterialAiTag = (tag: MaterialAiTag) => {
        this.$data.update((prev) => {
            const newTags = prev.materialAiTags ?? []
            const index = newTags.indexOf(tag)
            if (index !== -1) {
                newTags.splice(index, 1)
            }
            return {...prev, materialAiTags: newTags}
        })
    }

    onConfirm = async () => {
        const input = this.$validatedInputData()
        if (!input) return

        this.$isLoading.set(true)
        this.dialogRef.disableClose = true
        let created: CreatedMaterialType | undefined
        try {
            switch (input.createMethod) {
                case "BATCH": {
                    created = {
                        createMethod: "BATCH",
                        item: {
                            organizationId: input.organizationId,
                            materialRangeTagId: input.materialRangeTagId,
                        },
                    }
                    break
                }
                case "BLANK": {
                    created = {
                        createMethod: "SINGLE",
                        item: await this.createMaterial({
                            name: input.name,
                            organizationId: input.organizationId,
                            materialRangeTagId: input.materialRangeTagId,
                        }),
                    }
                    break
                }
                case "FROM_IMAGE": {
                    created = {
                        createMethod: "SINGLE",
                        item: await this.createMaterialWithTextures(input),
                    }
                    break
                }
                case "FROM_IMAGE_AI": {
                    created = {
                        createMethod: "SINGLE",
                        item: await this.createMaterialWithTextures(input),
                    }
                    await this.startMaterialAiJob(created.item, {ignoreDpi: input.materialAiIgnoreDpi, tags: input.materialAiTags})
                    break
                }
                case "FROM_PBR_FILES": {
                    created = {
                        createMethod: "SINGLE",
                        item: await this.createMaterial({
                            name: input.name,
                            organizationId: input.organizationId,
                            materialRangeTagId: input.materialRangeTagId,
                            textureGroup: {
                                meshSpecific: false,
                                name: "Initial Texture Group",
                                textureSets: [
                                    {
                                        name: "Initial Texture Set",
                                    },
                                ],
                            },
                        }),
                    }
                    break
                }
            }
        } catch (err) {
            this.logger.error("Failed to create material", err)
            this.notifications.showError("Cannot create new item.")
        } finally {
            // Revoke any created object URLs
            const pbrFiles = this.$data().pbrFiles
            if (pbrFiles) {
                for (const {imageUrl} of pbrFiles.values()) {
                    if (imageUrl) URL.revokeObjectURL(imageUrl)
                }
            }

            this.$isLoading.set(false)
            this.dialogRef.disableClose = false
            this.dialogRef.close(created)
        }
    }

    private async createMaterial(data: {
        name: string
        organizationId: string
        materialRangeTagId?: string
        textureGroup?: {
            meshSpecific: boolean
            name: string
            textureSets: {name: string}[]
        }
    }) {
        const {name, organizationId, materialRangeTagId, textureGroup} = data
        const result = await mutateThrowingErrors(this.createMaterialGql)({
            input: {
                name,
                organizationId,
                tagAssignments: materialRangeTagId ? [materialRangeTagId] : undefined,
                state: MaterialState.Draft,
                paymentState: PaymentState.OrderPlaced,
                meshSpecific: false,
                nextActor: NextActor.Team1,
                textureGroup,
            },
        })
        return result.createMaterial
    }

    private async createMaterialWithTextures(
        data: Extract<AddMaterialData, {createMethod: "FROM_IMAGE" | "FROM_IMAGE_AI"}>,
    ): Promise<AddMaterialItemWithTextureSetRevisionsFragment> {
        const {name, materialRangeTagId, organizationId, pbrFiles, width, height} = data

        const uploadedTextures = await Promise.all(
            Array.from(pbrFiles.entries()).map(async ([textureType, {file, colorSpace}]) => {
                if (!file) throw new Error(`File for texture type ${textureType} is required`)
                const dataObject = await this.uploadService.createAndUploadDataObject(
                    file,
                    {
                        organizationId,
                        type: DataObjectType.Texture,
                        imageDataType: imageDataTypeFromTextureType(textureType),
                        imageColorSpace: colorSpace ?? ImageColorSpace.Srgb,
                    },
                    {processUpload: true, showUploadToolbar: true},
                )
                return {textureType, dataObjectId: dataObject.id}
            }),
        )

        const result = await mutateThrowingErrors(this.createMaterialIncludeTexturesGql)({
            input: {
                name,
                organizationId,
                tagAssignments: materialRangeTagId ? [materialRangeTagId] : undefined,
                state: MaterialState.Draft,
                paymentState: PaymentState.OrderPlaced,
                meshSpecific: false,
                nextActor: NextActor.Team1,
                textureGroup: {
                    meshSpecific: false,
                    name: "Initial Texture Group",
                    textureSets: [
                        {
                            name: "Initial Texture Set",
                            textureSetRevisions: [
                                {
                                    name: "Initial Texture Set Revision",
                                    width,
                                    height,
                                    mapAssignments: uploadedTextures,
                                },
                            ],
                        },
                    ],
                },
            },
        })
        return result.createMaterial
    }

    private async startMaterialAiJob(
        newMaterial: AddMaterialItemWithTextureSetRevisionsFragment,
        {ignoreDpi = false, tags = []}: {ignoreDpi?: boolean; tags?: MaterialAiTag[]},
    ) {
        const textureSetRevisionId = newMaterial.textureGroup?.textureSets[0]?.textureSetRevisions[0]?.id
        if (!textureSetRevisionId) {
            return
        }
        await this.texturesApiService.startMaterialAiJob({
            textureSetRevisionId,
            generateDisplacement: undefined,
            tags,
            ignoreDpi,
            filterRadius: undefined,
        })
        this.notifications.showInfo("Material AI job started successfully.")
    }
}
